<template>
  <div>
    <b-card>
      <!-- Header and Filters -->
      <div>
        <b-row>
          <b-col
            cols="12"
            md="12"
          >
            <h2>{{ $t("reports.rejected-invoices-history") }}</h2>
            <br>
          </b-col>

          <!-- Date Range Filters (YYYY-MM-DD) -->
          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.from-date')"
              label-for="fromDate"
            >
              <b-form-input
                id="fromDate"
                v-model="fromDate"
                type="date"
                :placeholder="'YYYY-MM-DD'"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.to-date')"
              label-for="toDate"
            >
              <b-form-input
                id="toDate"
                v-model="toDate"
                type="date"
                :placeholder="'YYYY-MM-DD'"
              />
            </b-form-group>
          </b-col>

          <!-- Invoice Type Filter -->
          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.invoice-type')"
              label-for="invoiceType"
            >
              <v-select
                v-model="invoiceTypeFilter"
                :placeholder="$t('common.invoice-type')"
                :options="invoiceTypes"
                :reduce="(item) => item.value"
              />
            </b-form-group>
          </b-col>

          <!-- Patient Name Filter -->
          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.patient-name')"
              label-for="patientName"
            >
              <b-form-input
                id="patientName"
                v-model="patientName"
                :placeholder="$t('common.patient-name')"
              />
            </b-form-group>
          </b-col>

          <!-- Invoice Number Filter -->
          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('reports.invoice-number')"
              label-for="invoiceNo"
            >
              <b-form-input
                id="invoiceNo"
                v-model="invoiceNo"
                type="number"
                :placeholder="$t('reports.invoice-number')"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center   mb-1 mb-md-0"
          >
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="resetFilters()"
            >
              <b-icon-arrow-clockwise class="mr-1" />
              {{ $t("common.reset") }}
            </b-button>

            <b-button
              variant="primary"
              @click="getReportData()"
            >
              {{ $t("common.search") }}
              <b-icon-search class="ml-1" />
            </b-button>
          </b-col>
        </b-row>

        <!-- Statistics Cards -->
        <b-row
          v-if="!loading && data.length > 0"
          class="mt-4"
        >
          <b-col
            md="3"
            cols="12"
            class="mb-3"
          >
            <b-card class="stats-card text-center">
              <h6 class="text-muted">
                {{ $t("reports.total-rejected") }}
              </h6>
              <h3 class="font-weight-bold text-danger">
                {{ dataCount }}
              </h3>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="12"
            class="mb-3"
          >
            <b-card class="stats-card text-center">
              <h6 class="text-muted">
                {{ $t("reports.total-amount") }}
              </h6>
              <h3 class="font-weight-bold text-primary">
                {{ formatCurrency(totalAmount) }}
              </h3>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="12"
            class="mb-3"
          >
            <b-card class="stats-card text-center">
              <h6 class="text-muted">
                {{ $t("reports.total-net-amount") }}
              </h6>
              <h3 class="font-weight-bold text-success">
                {{ formatCurrency(totalNetAmount) }}
              </h3>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="12"
            class="mb-3"
          >
            <b-card class="stats-card text-center">
              <h6 class="text-muted">
                {{ $t("reports.total-discount") }}
              </h6>
              <h3 class="font-weight-bold text-warning">
                {{ formatCurrency(totalDiscount) }}
              </h3>
            </b-card>
          </b-col>
        </b-row>

        <!-- Data Table -->
        <div class="d-flex justify-content-between mt-4 mb-2">
          <h2>{{ $t("reports.rejected-invoices-list") }}</h2>
        </div>

        <b-table
          v-if="!loading"
          :items="data"
          responsive
          :fields="tableColumns"
          primary-key="invoice_id"
          show-empty
          :empty-text="$t('common.no-matching-results')"
          class="position-relative"
          striped
          hover
        >
          <!-- Invoice Number -->
          <template #cell(invoice_no)="row">
            <div
              class="text-nowrap font-weight-bold"
              style="color: #007bff"
            >
              #{{ row.item.invoice_no }}
            </div>
          </template>

          <!-- Invoice Type -->
          <template #cell(type_label)="row">
            <b-badge
              :variant="getInvoiceTypeVariant(row.item.type)"
              class="p-2"
            >
              {{ getInvoiceTypeDisplayLabel(row.item.type, row.item.type_label) }}
            </b-badge>
          </template>

          <!-- Patient Info -->
          <template #cell(patient_name)="row">
            <div>
              <div class="font-weight-bold">
                {{ row.item.patient_name }}
              </div>
              <small class="text-muted">
                {{ $t("common.card-no") }}: {{ row.item.patient_card_no }}
              </small>
            </div>
          </template>

          <!-- Amount -->
          <template #cell(amount)="row">
            <div class="text-nowrap">
              {{ formatCurrency(row.item.amount) }}
            </div>
          </template>

          <!-- Discount -->
          <template #cell(discount)="row">
            <div class="text-nowrap text-warning">
              {{ formatCurrency(row.item.discount) }}
            </div>
          </template>

          <!-- Net Amount -->
          <template #cell(net_amount)="row">
            <div class="text-nowrap font-weight-bold text-success">
              {{ formatCurrency(row.item.net_amount) }}
            </div>
          </template>

          <!-- Created By -->
          <template #cell(created_by_name)="row">
            <div>
              <div>{{ row.item.created_by_name }}</div>
              <small class="text-muted">{{ formatDate(row.item.created_at) }}</small>
            </div>
          </template>

          <!-- Rejected Info -->
          <template #cell(rejected_info)="row">
            <div>
              <div class="text-danger">
                {{ row.item.rejected_by_name }}
              </div>
              <small class="text-muted">
                {{ formatDate(row.item.rejected_at) }}
              </small>
            </div>
          </template>

          <!-- Description -->
          <template #cell(description)="row">
            <div class="description-cell">
              {{ row.item.description }}
            </div>
          </template>

          <!-- Actions -->
          <template #cell(actions)="row">
            <div class="text-nowrap d-flex flex-wrap gap-1">
              <!-- <b-button
                variant="info"
                size="sm"
                @click="viewInvoiceDetails(row.item)"
              >
                <b-icon-eye class="mr-1" />
                {{ $t("common.view") }}
              </b-button> -->
              <b-button
                variant="outline-primary"
                size="sm"
                @click="navigateToPatientInvoices(row.item)"
              >
                <b-icon-receipt class="mr-1" />
                معاينه حسابات المريض
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Loading Spinner -->
        <template v-if="loading">
          <div>
            <div class="d-flex justify-content-center mb-1">
              <b-spinner
                label="Loading..."
                variant="primary"
              />
            </div>
            <div class="text-center text-muted">
              {{ $t("common.loading") }}...
            </div>
          </div>
        </template>

        <!-- Pagination -->
        <div
          v-if="!loading && data.length > 0"
          class="mx-2 mb-2"
        >
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">
                {{ $t("common.showing") }} {{ ((page - 1) * take) + 1 }}
                {{ $t("common.to") }}
                {{ Math.min(page * take, dataCount) }}
                {{ $t("common.of") }} {{ dataCount }}
                {{ $t("reports.invoices") }}
              </span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="page"
                :total-rows="dataCount"
                :per-page="take"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
                @input="getReportData()"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-card>

    <!-- Invoice Details Modal -->
    <b-modal
      v-model="showDetailsModal"
      :title="$t('reports.invoice-details')"
      size="lg"
      ok-only
      :ok-title="$t('common.close')"
    >
      <div v-if="selectedInvoice">
        <b-row>
          <b-col md="6">
            <p><strong>{{ $t("reports.invoice-number") }}:</strong> #{{ selectedInvoice.invoice_no }}</p>
            <p><strong>{{ $t("common.invoice-type") }}:</strong> {{ getInvoiceTypeDisplayLabel(selectedInvoice.type, selectedInvoice.type_label) }}</p>
            <p><strong>{{ $t("common.patient-name") }}:</strong> {{ selectedInvoice.patient_name }}</p>
            <p><strong>{{ $t("common.card-no") }}:</strong> {{ selectedInvoice.patient_card_no }}</p>
          </b-col>
          <b-col md="6">
            <p><strong>{{ $t("reports.amount") }}:</strong> {{ formatCurrency(selectedInvoice.amount) }}</p>
            <p><strong>{{ $t("common.discount") }}:</strong> {{ formatCurrency(selectedInvoice.discount) }}</p>
            <p><strong>{{ $t("reports.net-amount") }}:</strong> {{ formatCurrency(selectedInvoice.net_amount) }}</p>
          </b-col>
        </b-row>
        <hr>
        <b-row>
          <b-col md="6">
            <p><strong>{{ $t("reports.created-by") }}:</strong> {{ selectedInvoice.created_by_name }}</p>
            <p><strong>{{ $t("reports.created-at") }}:</strong> {{ formatDateTime(selectedInvoice.created_at) }}</p>
          </b-col>
          <b-col md="6">
            <p><strong>{{ $t("reports.rejected-by") }}:</strong> {{ selectedInvoice.rejected_by_name }}</p>
            <p><strong>{{ $t("reports.rejected-at") }}:</strong> {{ formatDateTime(selectedInvoice.rejected_at) }}</p>
          </b-col>
        </b-row>
        <hr>
        <b-row>
          <b-col cols="12">
            <p><strong>{{ $t("common.description") }}:</strong></p>
            <p class="text-muted">
              {{ selectedInvoice.description }}
            </p>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BPagination,
  BSpinner,
  BBadge,
  BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import permissionChecker from '@/mixins/permissionChecker'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    ToastificationContent,
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BPagination,
    BSpinner,
    BBadge,
    BModal,
    vSelect,
  },
  mixins: [permissionChecker],
  data() {
    return {
      take: 15,
      loading: false,
      dataCount: 0,
      data: [],
      page: 1,
      fromDate: '',
      toDate: '',
      invoiceTypeFilter: null,
      patientName: '',
      invoiceNo: '',
      showDetailsModal: false,
      selectedInvoice: null,
      totalAmount: 0,
      totalNetAmount: 0,
      totalDiscount: 0,
      invoiceTypes: [
        { value: null, label: this.$t('common.all') },
        { value: 'invoice_out_clinic', label: this.$t('reports.invoice-out-clinic') },
        { value: 'invoice_insurance_surgery', label: this.$t('reports.invoice-insurance-surgery') },
        { value: 'invoice_nicu', label: this.$t('reports.invoice-nicu') },
        { value: 'invoice_surgery', label: this.$t('reports.invoice-surgery') },
        { value: 'invoice_clinic', label: this.$t('reports.invoice-clinic') },
      ],
      typeMap: {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        refund: 'cashiers.invoice-type-refund',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        invoice_emergency: 'cashiers.invoice-type-emergency',
      },
      tableColumns: [
        {
          key: 'invoice_no',
          sortable: true,
          label: this.$t('reports.invoice-number'),
          thStyle: { width: '100px' },
        },
        {
          key: 'type_label',
          sortable: true,
          label: this.$t('common.invoice-type'),
          thStyle: { width: '180px' },
        },
        {
          key: 'patient_name',
          sortable: true,
          label: this.$t('common.patient-name'),
          thStyle: { width: '150px' },
        },
        {
          key: 'amount',
          sortable: true,
          label: this.$t('reports.amount'),
          thStyle: { width: '120px' },
        },
        {
          key: 'discount',
          sortable: true,
          label: this.$t('common.discount'),
          thStyle: { width: '100px' },
        },
        {
          key: 'net_amount',
          sortable: true,
          label: this.$t('reports.net-amount'),
          thStyle: { width: '120px' },
        },
        {
          key: 'created_by_name',
          sortable: true,
          label: this.$t('reports.created-by'),
          thStyle: { width: '150px' },
        },
        {
          key: 'rejected_info',
          sortable: false,
          label: this.$t('reports.rejected-info'),
          thStyle: { width: '150px' },
        },
        {
          key: 'description',
          sortable: false,
          label: this.$t('common.description'),
          thStyle: { width: '200px' },
        },
        {
          key: 'actions',
          label: this.$t('common.actions'),
          thStyle: { width: '220px' },
        },
      ],
    }
  },
  mounted() {
    this.setDefaultDates()
    this.getReportData()
  },
  methods: {
    setDefaultDates() {
      const today = new Date()
      const firstDayOfYear = new Date(today.getFullYear(), 0, 1)
      this.fromDate = firstDayOfYear.toISOString().split('T')[0]
      this.toDate = today.toISOString().split('T')[0]
    },

    formatDateForQuery(val) {
      if (!val) return ''
      if (typeof val === 'string') return val
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    async getReportData() {
      if (!this.fromDate || !this.toDate) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.validation-error'),
            text: this.$t('reports.date-range-required'),
            variant: 'warning',
          },
        })
        return
      }

      this.loading = true

      // Build API URL using normal query params (no filter[])
      const from = this.formatDateForQuery(this.fromDate)
      const to = this.formatDateForQuery(this.toDate)
      let url = `/reports/rejected-invoices?from=${from}&to=${to}&page=${this.page}&take=${this.take}`

      if (this.invoiceTypeFilter) {
        url += `&invoice_type=${encodeURIComponent(this.invoiceTypeFilter)}`
      }
      if (this.patientName && this.patientName.trim()) {
        url += `&patient_name=${encodeURIComponent(this.patientName.trim())}`
      }
      if (this.invoiceNo !== '' && this.invoiceNo != null) {
        url += `&invoice_no=${encodeURIComponent(String(this.invoiceNo))}`
      }

      try {
        await this.getList(url)

        if (this.all_data && this.all_data.data) {
          this.dataCount = this.all_data.data.total || 0
          this.data = this.all_data.data.invoices || []

          // Calculate totals
          this.calculateTotals()
        }
      } catch (error) {
        console.error('Error fetching report data:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            text: this.$t('common.error-fetching-data'),
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    calculateTotals() {
      this.totalAmount = this.data.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
      this.totalNetAmount = this.data.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0)
      this.totalDiscount = this.data.reduce((sum, item) => sum + parseFloat(item.discount || 0), 0)
    },

    resetFilters() {
      this.setDefaultDates()
      this.invoiceTypeFilter = null
      this.patientName = ''
      this.invoiceNo = ''
      this.page = 1
      this.getReportData()
    },

    formatCurrency(amount) {
      return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount || 0)} IQD`
    },

    formatDate(dateString) {
      if (!dateString || dateString === 'N/A') return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    formatDateTime(dateString) {
      if (!dateString || dateString === 'N/A') return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    getInvoiceTypeVariant(type) {
      const variants = {
        invoice_out_clinic: 'primary',
        invoice_insurance_surgery: 'success',
        invoice_nicu: 'warning',
        invoice_surgery: 'info',
        invoice_clinic: 'secondary',
      }
      return variants[type] || 'dark'
    },

    getInvoiceTypeDisplayLabel(type, fallback) {
      if (this.typeMap[type]) {
        return this.$t(this.typeMap[type])
      }
      return fallback != null && fallback !== '' ? fallback : (type || '—')
    },

    viewInvoiceDetails(invoice) {
      this.selectedInvoice = invoice
      this.showDetailsModal = true
    },

    navigateToPatientInvoices(item) {
      const patientId = item.patient_id
      const patientCardId = item.patient_card_id
      if (!patientId || !patientCardId) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            text: this.$t('common.error-fetching-data'),
            variant: 'warning',
          },
        })
        return
      }
      this.$router.push({
        name: 'acounting-invoices',
        params: {
          patientId,
          patientCardId,
          isPatientAccounting: 'false',
          isSurgeryAccounting: 'true',
        },
      })
    },

    exportToExcel() {
      // Implement Excel export functionality
      // You can use a library like xlsx or export-from-json
      console.log('Export to Excel')

      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t('common.success'),
          text: this.$t('reports.export-started'),
          variant: 'success',
        },
      })
    },
  },
}
</script>

  <style scoped>
  .stats-card {
    border-left: 4px solid #007bff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s;
  }

  .stats-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .description-cell {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .description-cell:hover {
    white-space: normal;
    overflow: visible;
  }
  </style>

  <style lang="scss">
  @import "@core/scss/vue/libs/vue-select.scss";
  </style>
